import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { AlertCircle, Mail, Lock, MessageCircle } from "lucide-react";

const SUPABASE_CALLBACK_PATH = "/login/supabase-callback";
const SUPABASE_REDIRECT_ORIGIN =
  typeof import.meta.env.VITE_APP_URL === "string" && import.meta.env.VITE_APP_URL
    ? import.meta.env.VITE_APP_URL.replace(/\/$/, "")
    : typeof window !== "undefined"
      ? window.location.origin
      : "";

function buildSupabaseEmailRedirectUrl(): string {
  if (!SUPABASE_REDIRECT_ORIGIN) return "";
  const next = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("next")
    : null;
  const base = `${SUPABASE_REDIRECT_ORIGIN}${SUPABASE_CALLBACK_PATH}`;
  return next ? `${base}?next=${encodeURIComponent(next)}` : base;
}

function mapAuthErrorMessage(message: string): string {
  const m = message.toLowerCase();
  if (m.includes("email not confirmed") || m.includes("email_not_confirmed") || m.includes("not confirmed")) {
    return "이메일 인증이 완료되지 않았습니다. 받은 편지함의 확인 링크를 눌러 주세요.";
  }
  if (m.includes("invalid login credentials") || m.includes("invalid credentials")) {
    return "이메일 또는 비밀번호가 올바르지 않습니다.";
  }
  if (m.includes("user already registered") || m.includes("already been registered")) {
    return "이미 가입된 이메일입니다. 로그인을 이용해 주세요.";
  }
  if (m.includes("signups not allowed") || m.includes("user not found")) {
    return "해당 이메일로는 링크를 보낼 수 없습니다. 이메일 가입을 먼저 완료했는지 확인해 주세요.";
  }
  return message;
}

function isUnconfirmedEmailError(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes("email not confirmed") ||
    m.includes("email_not_confirmed") ||
    m.includes("confirmation required") ||
    (m.includes("not confirmed") && m.includes("email"))
  );
}

export function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithSupabase, isAuthenticated } = useAuth();
  const [error, setError] = React.useState("");
  const [info, setInfo] = React.useState("");
  const [mode, setMode] = React.useState<"signin" | "signup">(() =>
    searchParams.get("signup") === "1" ? "signup" : "signin"
  );
  const [formData, setFormData] = React.useState({ email: "", password: "" });
  const [loading, setLoading] = React.useState(false);
  const [offerResendSignup, setOfferResendSignup] = React.useState(false);
  const [resendLoading, setResendLoading] = React.useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = React.useState(false);
  /** 이메일·소셜 가입 시 만 14세 이상 및 방침 확인 (개인정보처리방침 정합성) */
  const [signupAgeConfirmed, setSignupAgeConfirmed] = React.useState(false);

  React.useEffect(() => {
    if (searchParams.get("signup") === "1") setMode("signup");
  }, [searchParams]);

  React.useEffect(() => {
    if (isAuthenticated) {
      const next = searchParams.get("next") || "/";
      navigate(next);
    }
  }, [isAuthenticated, navigate, searchParams]);

  React.useEffect(() => {
    const err = searchParams.get("error");
    if (err === "no_supabase_session") setError("로그인 세션을 불러올 수 없습니다. 다시 로그인해 주세요.");
    else if (err === "supabase_not_configured") setError("로그인 기능을 일시적으로 사용할 수 없습니다. 관리자에게 문의해 주세요.");
  }, [searchParams]);

  const signupGuardMessage =
    "회원가입을 진행하려면 만 14세 이상 확인 및 개인정보처리방침 확인에 동의해 주세요.";

  const handleSupabaseGoogle = () => {
    if (mode === "signup" && !signupAgeConfirmed) {
      setError(signupGuardMessage);
      return;
    }
    if (!supabase || !SUPABASE_REDIRECT_ORIGIN) return;
    const next = searchParams.get("next");
    const callback =
      next != null && next !== ""
        ? `${SUPABASE_REDIRECT_ORIGIN}${SUPABASE_CALLBACK_PATH}?next=${encodeURIComponent(next)}`
        : `${SUPABASE_REDIRECT_ORIGIN}${SUPABASE_CALLBACK_PATH}`;
    supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: callback },
    });
  };

  const handleSupabaseKakao = () => {
    if (mode === "signup" && !signupAgeConfirmed) {
      setError(signupGuardMessage);
      return;
    }
    if (!supabase || !SUPABASE_REDIRECT_ORIGIN) return;
    const next = searchParams.get("next");
    const callback =
      next != null && next !== ""
        ? `${SUPABASE_REDIRECT_ORIGIN}${SUPABASE_CALLBACK_PATH}?next=${encodeURIComponent(next)}`
        : `${SUPABASE_REDIRECT_ORIGIN}${SUPABASE_CALLBACK_PATH}`;
    supabase.auth.signInWithOAuth({
      provider: "kakao",
      options: { redirectTo: callback },
    });
  };

  const getEmailRedirectTo = (): string | null => {
    const url = buildSupabaseEmailRedirectUrl();
    return url || null;
  };

  const handleResendSignupEmail = async () => {
    setError("");
    setInfo("");
    if (!supabase) {
      setError("로그인 기능을 일시적으로 사용할 수 없습니다. 관리자에게 문의해 주세요.");
      return;
    }
    const email = formData.email.trim();
    if (!email) {
      setError("이메일을 입력해 주세요.");
      return;
    }
    const emailRedirectTo = getEmailRedirectTo();
    if (!emailRedirectTo) {
      setError("로그인 주소 설정에 문제가 있습니다. 관리자에게 문의해 주세요.");
      return;
    }
    setResendLoading(true);
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: "signup",
        email,
        options: { emailRedirectTo },
      });
      if (resendError) {
        setError(mapAuthErrorMessage(resendError.message));
        return;
      }
      setInfo(
        "가입 확인 메일을 다시 보냈습니다. 메일함과 스팸함을 확인해 주세요. 잠시 후에도 오지 않으면 이메일 주소가 맞는지 확인하시고, 문제가 계속되면 사이트 관리자에게 문의해 주세요."
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "요청에 실패했습니다.";
      setError(mapAuthErrorMessage(msg));
    } finally {
      setResendLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setError("");
    setInfo("");
    if (!supabase) {
      setError("비밀번호 재설정 기능을 일시적으로 사용할 수 없습니다. 관리자에게 문의해 주세요.");
      return;
    }
    const email = formData.email.trim();
    if (!email) {
      setError("이메일을 입력해 주세요.");
      return;
    }
    
    let redirectUrl = buildSupabaseEmailRedirectUrl();
    if (redirectUrl) {
      try {
        const urlObj = new URL(redirectUrl);
        urlObj.pathname = "/update-password";
        redirectUrl = urlObj.toString();
      } catch (e) {
        redirectUrl = window.location.origin + "/update-password";
      }
    } else {
      redirectUrl = window.location.origin + "/update-password";
    }

    setResetPasswordLoading(true);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });
      if (resetError) {
        setError(mapAuthErrorMessage(resetError.message));
        return;
      }
      setInfo(
        "비밀번호 재설정 링크를 메일로 보냈습니다. 메일의 링크를 누르면 비밀번호를 새로 설정할 수 있습니다. 스팸함도 확인해 주세요."
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "요청에 실패했습니다.";
      setError(mapAuthErrorMessage(msg));
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setOfferResendSignup(false);
    if (!supabase) {
      setError("로그인 기능을 일시적으로 사용할 수 없습니다. 관리자에게 문의해 주세요.");
      return;
    }
    const emailRedirectTo = buildSupabaseEmailRedirectUrl();
    if (!emailRedirectTo) {
      setError("로그인 주소 설정에 문제가 있습니다. 관리자에게 문의해 주세요.");
      return;
    }

    setLoading(true);
    try {
      if (mode === "signup") {
        if (!signupAgeConfirmed) {
          setError(signupGuardMessage);
          setLoading(false);
          return;
        }
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email: formData.email.trim(),
          password: formData.password,
          options: { emailRedirectTo },
        });
        if (signUpError) {
          setError(mapAuthErrorMessage(signUpError.message));
          return;
        }
        const user = signUpData.user;
        const session = signUpData.session;
        // 이미 동일 이메일로 가입된 경우 Supabase가 에러 대신 identities 없이 user만 줄 수 있음(노출 완화).
        if (user && Array.isArray(user.identities) && user.identities.length === 0) {
          setOfferResendSignup(true);
          setError("");
          setInfo(
            "이 메일은 이미 가입된 계정일 수 있습니다. 같은 주소로 다시 가입하면 보통 새 가입 확인 메일이 가지 않습니다(계정 보호 정책).\n\n아래「비밀번호를 잊으셨나요? (비밀번호 재설정)」를 누르면 이 주소로 재설정 링크가 갑니다. 링크에서 새 비밀번호를 설정한 뒤「이메일 로그인」으로 들어오시면 기존 프로필과 연결됩니다. 비밀번호를 이미 아시면 로그인 탭에서 바로 로그인하세요.\n\n가입만 하시고 아직 메일 인증을 못 하신 경우에만「가입 확인 메일 다시 받기」를 눌러 보세요."
          );
          return;
        }
        // Confirm email이 꺼져 있거나 즉시 확인된 환경이면 세션이 바로 옴 → 메일 없이 로그인 처리.
        if (session?.access_token) {
          await loginWithSupabase(session.access_token);
          const next = searchParams.get("next") || "/";
          navigate(next);
          return;
        }
        setInfo(
          "가입 확인 메일을 보냈습니다. 메일의 링크로 인증한 뒤「이메일 로그인」으로 들어오세요. 스팸함도 확인해 주세요.\n\n몇 분 내로 오지 않으면 이메일 주소를 다시 확인해 주시고, 이미 가입하신 적이 있다면 로그인을 이용해 주세요. 그래도 해결되지 않으면 사이트 관리자에게 문의해 주세요."
        );
        setFormData((f) => ({ ...f, password: "" }));
        return;
      }

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email.trim(),
        password: formData.password,
      });
      if (signInError) {
        if (isUnconfirmedEmailError(signInError.message)) {
          setOfferResendSignup(true);
        }
        setError(mapAuthErrorMessage(signInError.message));
        return;
      }
      const token = data.session?.access_token;
      if (!token) {
        setOfferResendSignup(true);
        setError("이메일 인증이 완료되지 않았습니다. 받은 편지함의 확인 링크를 눌러 주세요.");
        return;
      }
      await loginWithSupabase(token);
      const next = searchParams.get("next") || "/";
      navigate(next);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "요청에 실패했습니다.";
      if (isUnconfirmedEmailError(msg)) {
        setOfferResendSignup(true);
      }
      setError(mapAuthErrorMessage(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fe-login min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="fe-brand-mark fe-login-brand">
            <span aria-hidden="true">f.</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">다시 만나 반가워요.</h1>
          <p className="mt-2 text-sm text-gray-600">
            Foreducator에 오신 것을 진심으로 환영합니다
          </p>
        </div>

        <div className="fe-login-panel p-6 sm:p-8 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {info && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 whitespace-pre-line">
              {info}
            </div>
          )}

          <div className="space-y-3">
            <button
              type="button"
              onClick={handleSupabaseGoogle}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="font-medium text-gray-700">Google로 로그인</span>
            </button>

            <button
              type="button"
              onClick={handleSupabaseKakao}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#3C1E1E">
                <path d="M12 3c5.799 0 10.5 3.664 10.5 8.185 0 4.52-4.701 8.184-10.5 8.184a13.5 13.5 0 0 1-1.727-.11l-4.408 2.399c-.501.265-.678.236-.472-.413l.892-3.678c-2.88-1.46-4.785-3.99-4.785-6.866C1.5 6.665 6.201 3 12 3z" />
              </svg>
              <span className="font-medium text-gray-700">카카오로 로그인</span>
            </button>
          </div>

          {mode === "signup" && (
            <label className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50/80 px-4 py-3 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={signupAgeConfirmed}
                onChange={(e) => {
                  setSignupAgeConfirmed(e.target.checked);
                  setError("");
                }}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>
                <span className="font-medium text-red-600">[필수]</span> 만 14세 이상이며,{" "}
                <Link to="/privacy" className="text-[#52673a] underline hover:text-[#35422c]" target="_blank" rel="noopener noreferrer">
                  개인정보처리방침
                </Link>
                을 확인하였습니다.
              </span>
            </label>
          )}

          <div className="pt-6 mt-4 border-t border-gray-200 space-y-3">
            <div className="flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
              <button
                type="button"
                aria-pressed={mode === "signin"}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                  mode === "signin" ? "bg-white shadow text-gray-900" : "text-gray-600"
                }`}
                onClick={() => {
                  setMode("signin");
                  setInfo("");
                  setError("");
                  setOfferResendSignup(false);
                }}
              >
                이메일 로그인
              </button>
              <button
                type="button"
                aria-pressed={mode === "signup"}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                  mode === "signup" ? "bg-white shadow text-gray-900" : "text-gray-600"
                }`}
                onClick={() => {
                  setMode("signup");
                  setSignupAgeConfirmed(false);
                  setInfo("");
                  setError("");
                  setOfferResendSignup(false);
                }}
              >
                이메일 가입
              </button>
            </div>

            <form onSubmit={handleEmailAuth} className="space-y-4" autoComplete="on">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  이메일
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      setOfferResendSignup(false);
                    }}
                    className="fe-input w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="이메일 주소"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  비밀번호
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete={mode === "signup" ? "new-password" : "current-password"}
                    required
                    minLength={6}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="fe-input w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={mode === "signup" ? "6자 이상" : "비밀번호"}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || resendLoading || resetPasswordLoading}
                className="fe-primary-action w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "처리 중..." : mode === "signup" ? "가입 확인 메일 받기" : "로그인"}
              </button>
            </form>

            <div className="flex flex-col gap-2">
              {(mode === "signin" || offerResendSignup) && (
                <button
                  type="button"
                  disabled={resetPasswordLoading || loading || resendLoading}
                  onClick={handleResetPassword}
                  className="w-full py-2.5 px-4 text-sm font-medium border-2 border-orange-200 text-orange-700 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resetPasswordLoading ? "보내는 중…" : "비밀번호를 잊으셨나요? (비밀번호 재설정)"}
                </button>
              )}
              {offerResendSignup && (
                <button
                  type="button"
                  disabled={resendLoading || loading || resetPasswordLoading}
                  onClick={handleResendSignupEmail}
                  className="w-full py-2.5 px-4 text-sm font-medium border border-[#d8dfc8] text-[#485537] rounded-xl hover:bg-[#edf0e3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendLoading ? "보내는 중…" : "가입 확인 메일 다시 받기 (아직 메일 인증 전인 경우)"}
                </button>
              )}
            </div>
          </div>
        </div>

        <a
          href="https://open.kakao.com/o/g3KNSC4e"
          target="_blank"
          rel="noopener noreferrer"
          className="group block px-4 py-3 rounded-xl border border-gray-200/70 bg-white/50 hover:bg-white/80 hover:border-gray-300/80 transition-all text-center"
        >
          <p className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
            <span className="inline-flex items-center justify-center gap-1.5 font-medium text-gray-700">
              <MessageCircle className="w-4 h-4 text-gray-400 group-hover:text-amber-600 transition-colors" />
              Foreducator 오픈채팅
            </span>
            <span className="block mt-1 text-xs text-gray-500 leading-relaxed">
              궁금한 점 · 최신 소식 · 사용 후기를 편하게 나눠요
            </span>
          </p>
        </a>
      </div>
    </div>
  );
}
